/**
 * useDashboardData Hook
 * 
 * Aggregates data from dashboard aggregation endpoints.
 * Uses server-side aggregation for accuracy and performance.
 * Supports date range filtering for stats and charts.
 */

import { useState, useEffect, useCallback } from 'react';
import type { DateRange } from '@/components/common/DateRangeSelector';
import { getDateRangeFromPreset } from '@/components/common/DateRangeSelector';
import { dashboardService } from '@/app/services/dashboardService';
import { fulfillmentService } from '@/app/services/fulfillmentService';
import type { ActivityItem } from '@/app/types/dashboard';
import { logger } from '@/app/utils/logger';

/**
 * Dashboard stats for the top cards
 */
export interface DashboardStats {
  pointsEarnedToday: number;
  pointsRedeemedToday: number;
  failedFulfillmentsCount: number;
}

/**
 * Daily points activity for chart
 */
export interface DailyPointsActivity {
  date: string;
  dayLabel: string;
  earned: number;
  redeemed: number;
}

/**
 * Top reward item
 */
export interface TopReward {
  rewardId: string;
  rewardName: string;
  category: string;
  redemptionCount: number;
  totalPoints: number;
  totalDollarValue: number;
  statusBreakdown: {
    pending: number;
    completed: number;
    failed: number;
  };
}

/**
 * Complete dashboard data
 */
export interface DashboardData {
  stats: DashboardStats;
  activity: ActivityItem[];
  pointsActivity: DailyPointsActivity[];
  topRewards: TopReward[];
}

export interface UseDashboardDataReturn {
  data: DashboardData | null;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Get day label (Mon, Tue, etc.)
 */
function getDayLabel(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { weekday: 'short' });
}

/**
 * Convert ISO datetime string to YYYY-MM-DD date string
 * Backend expects date-only format, not full ISO datetime
 */
function toDateOnly(isoDatetime: string | null | undefined): string | undefined {
  if (!isoDatetime) return undefined;
  return isoDatetime.split('T')[0];
}

// Default to Last 7 days
const DEFAULT_DATE_RANGE: DateRange = {
  preset: '7d',
  ...getDateRangeFromPreset('7d'),
};

export function useDashboardData(
  dateRange: DateRange = DEFAULT_DATE_RANGE,
  partnerProductId: number | null = null
): UseDashboardDataReturn {
  const [data, setData] = useState<DashboardData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const { startDate, endDate } = dateRange;

      // Convert ISO datetime strings to date-only format (YYYY-MM-DD) for backend
      const fromDate = toDateOnly(startDate);
      const toDate = toDateOnly(endDate);

      // Default fallback dates for points activity (required params)
      const defaultFromDate = toDateOnly(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString());
      const defaultToDate = toDateOnly(new Date().toISOString());

      // Convert null to undefined for service calls
      const partnerIdParam = partnerProductId ?? undefined;

      // Fetch all dashboard data in parallel using aggregation endpoints
      // Note: fulfillment status counts uses consolidated source for actionable failures
      const [statsResponse, fulfillmentStatusCounts, pointsActivityResponse, topRewardsResponse, recentActivityResponse] = await Promise.all([
        dashboardService.getStats(fromDate, toDate, partnerIdParam),
        fulfillmentService.getStatusCounts(),
        dashboardService.getPointsActivity(
          fromDate || defaultFromDate!,
          toDate || defaultToDate!,
          partnerIdParam
        ),
        dashboardService.getTopRewards(fromDate, toDate, partnerIdParam, 5),
        dashboardService.getRecentActivity(10, partnerIdParam),
      ]);

      // Transform stats response - use FAILED_ACTIONABLE from consolidated source
      const stats: DashboardStats = {
        pointsEarnedToday: statsResponse.points_earned,
        pointsRedeemedToday: statsResponse.points_redeemed,
        failedFulfillmentsCount: fulfillmentStatusCounts.FAILED_ACTIONABLE,
      };

      // Transform points activity for chart (add day labels)
      const pointsActivity: DailyPointsActivity[] = pointsActivityResponse.activity.map((day) => ({
        date: day.date,
        dayLabel: getDayLabel(day.date),
        earned: day.earned,
        redeemed: day.redeemed,
      }));

      // Transform top rewards (convert snake_case to camelCase)
      const topRewards: TopReward[] = topRewardsResponse.rewards.map((reward) => ({
        rewardId: reward.reward_id,
        rewardName: reward.reward_name,
        category: reward.category,
        redemptionCount: reward.redemption_count,
        totalPoints: reward.total_points,
        totalDollarValue: reward.total_dollar_value,
        statusBreakdown: reward.status_breakdown,
      }));

      // Transform recent activity (convert snake_case link_to to camelCase linkTo)
      const activity: ActivityItem[] = recentActivityResponse.activity.map((item) => ({
        id: item.id,
        type: item.type as ActivityItem['type'],
        title: item.title,
        description: item.description,
        timestamp: item.timestamp,
        points: item.points,
        status: item.status as ActivityItem['status'],
        linkTo: item.link_to, // Transform link_to to linkTo
        userId: item.user_id,
      }));

      setData({
        stats,
        activity,
        pointsActivity,
        topRewards,
      });
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load dashboard data';
      logger.error('Failed to fetch dashboard data', { error: err });
      setError(errorMessage);
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [dateRange, partnerProductId]);

  // Fetch data on mount and when date range or partner changes
  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  return {
    data,
    isLoading,
    error,
    refresh: fetchDashboardData,
  };
}
